import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import * as yup from 'yup';
import { ProgramCap } from '../../../types';

dayjs.extend(isBetween);

export interface ProgramCapFormValues {
  programCapId: number | null;
  clientId: number;
  startDate: Date;
  endDate: Date;
  cap: number;
}

// ACCOUNTS-AND-PLAN-MIGRATION: cap.clientId is client-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
export const ProgramCapValidationSchema = yup.object({
  programCapId: yup.number().nullable(),
  clientId: yup.number().nullable().required('Client is required'),
  startDate: yup
    .date()
    .typeError('Invalid date')
    .required('Start date is required')
    .test('startDate', 'Start date cannot be after end date', function (value) {
      const { endDate } = this.parent;
      if (!value || !endDate) return true;
      return new Date(value) <= new Date(endDate);
    })
    .test(
      'startDateOverlap',
      'Date range overlaps with an existing program cap',
      function (value) {
        const { clientId, programCapId } = this.parent;
        let programCaps = this.options.context?.programCaps ?? [];

        if (!value || !clientId) return true;

        const startDate = new Date(value);

        if (programCapId) {
          programCaps = programCaps.filter(
            (cap: ProgramCap) =>
              cap.id !== programCapId && cap.clientId === clientId,
          );
        }

        return !programCaps.some(
          (cap: ProgramCap) =>
            dayjs(startDate).isBetween(
              cap.startDate,
              cap.endDate,
              'day',
              '[]',
            ) && cap.clientId === clientId,
        );
      },
    ),
  endDate: yup
    .date()
    .typeError('Invalid date')
    .required('End date is required')
    .test('endDate', 'End date cannot be before start date', function (value) {
      const { startDate } = this.parent;
      if (!value || !startDate) return true;
      return new Date(value) >= new Date(startDate);
    })
    .test(
      'endDateOverlap',
      'Date range overlaps with an existing program cap',
      function (value) {
        const { clientId, programCapId } = this.parent;
        let programCaps = this.options.context?.programCaps ?? [];

        if (!value || !clientId) return true;

        const endDate = new Date(value);

        if (programCapId) {
          programCaps = programCaps.filter(
            (cap: ProgramCap) =>
              cap.id !== programCapId && cap.clientId === clientId,
          );
        }

        return !programCaps.some(
          (cap: ProgramCap) =>
            dayjs(endDate).isBetween(cap.startDate, cap.endDate, 'day', '[]') &&
            cap.clientId === clientId,
        );
      },
    ),
  cap: yup
    .number()
    .typeError('Cap must be a number')
    .required('Cap is required')
    .min(1, 'Cap must be greater than 0'),
}) as yup.ObjectSchema<ProgramCapFormValues>;
